import { default as Theme, teceoOrange, teceoWarningMain, default as theme } from '../../Theme';
import { BaseItem } from '../../components/ListV2/components/table/VirtualizedTable.types';
import AppEnvironment from '../../components/enums/appEnvironment.enum';
import LangEnum from '../../lang/LangEnum';
import { LangEnumOption } from '../../lib/OptionsLib';
import { UserTeamDTO } from '../../services/modules/users/users.types';
import { BrandSplitType } from '../brand-split-settings/brandSplitSettings.types';
import { Classification } from '../classifications/classification.types';
import { BrandIntegration } from '../context/ApplicationContext/applicationContext.types';
import { Consultant, Customer, Seller } from '../customer/types/customer.types';
import { OfflineStatus } from '../offline/domain/offlineOrder/offlineOrder.types';
import { PriceTable } from '../price-tables/price-table.types';
import { AvailabilityEnum, CompactAvailability, DeliveryWindow } from '../products/ProductTypes';
import { SalesRepresentative } from '../sales-representative/sales-representative.types';
import { User, UserStatus } from '../user-admin/user.types';
import { UserRole } from '../users/roles';
import { OrderAction } from './actions/types/orderActions.types';
import { SignedContract } from './contract/signed-contract';
import { OrderStatus } from './interfaces/order-status.interface';
import { OrderGatewayPaymentStatus } from './payment/enums/orderPaymentGatewayStatus.enum';
import { OrderPaymentStatus } from './payment/orderPayment.types';

export enum SynchronizedType {
  SUCCESS = 'SUCCESS',
  SUCCESS_RESET = 'SUCCESS_RESET',
  ERROR = 'ERROR',
  NOT_SYNCHRONIZED = 'NOT_SYNCHRONIZED',
  PROCESSING = 'PROCESSING',
  PROCESSING_AFTER_RESET = 'PROCESSING_AFTER_RESET',
  SUCCESS_WITH_PAYMENT_ERROR = 'SUCCESS_WITH_PAYMENT_ERROR',
}

export interface ListOrdersDTO {
  orderActions: OrderAction[];
  id: string;
  code: string;
  createdAt: Date;
  brand: GetBrandDTO;
  customer: Customer;
  seller: ListUserDTO;
  buyer: ListUserDTO;
  status: StatusType;
  archived: boolean;
  type: OrderType;
  totalValue: number;
  totalPieces: number;
  subTotal: number;
  availability: OrderType[];
  splitSettings: BrandSplitSettings[];
  splitted: boolean;
  synchronized: SynchronizedType;
  paymentStatus: OrderPaymentStatus;
  classification: Classification;
  deliveryWindow: DeliveryWindow;

  orderConditions: { id: string; paymentRequirePaymentGateway: boolean | null }[];

  orderGatewayPayments: {
    id: string;
    status: OrderGatewayPaymentStatus;
    createdAt: Date;
    updatedAt: Date;
  }[];
}

export interface ListUserDTO {
  id: string;
  email: string;
  phone: string;
  name: string;
  surname: string;
  cpf: string;
  profileUrl: string;
  teams: UserTeamDTO[];
  status: UserStatus;
  userId?: string; // form
}

export interface GetBrandDTO {
  id: string;
  createdAt: Date;
  logoUrl: string;
  active: boolean;
  commercialName: string;
  companyName: string;
  title: string;
  cnpj: string;
  stateInscription: string;
  phone: string;
  email: string;
  observation: string;
}

export type OrderType = 'PRE_ORDER' | 'READY_FOR_DELIVERY' | 'BOTH';

export type OrderTypeString = `${CompactAvailability}` | 'BOTH';

export enum OrderChargeTypeEnum {
  WALLET = 0,
  CHARGE_AT_THE_STORE_OR_COMPANY = 16,
  WAITING_RECEIVING = 17,
  DIRECT_BANK_SLIP = 18,
  EXPORT = 28,
}

export interface OrderChargeTypeOption {
  value: OrderChargeTypeEnum;
  label: LangEnum;
}

export const orderChargeTypesOptions: Array<OrderChargeTypeOption> = [
  {
    label: LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_WALLET_LABEL,
    value: OrderChargeTypeEnum.WALLET,
  },
  {
    label: LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_CHARGE_AT_THE_STORE_OR_COMPANY_LABEL,
    value: OrderChargeTypeEnum.CHARGE_AT_THE_STORE_OR_COMPANY,
  },
  {
    label: LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_WAITING_RECEIVING_LABEL,
    value: OrderChargeTypeEnum.WAITING_RECEIVING,
  },
  {
    label: LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_DIRECT_BANK_SLIP_LABEL,
    value: OrderChargeTypeEnum.DIRECT_BANK_SLIP,
  },
  {
    label: LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_EXPORT_LABEL,
    value: OrderChargeTypeEnum.EXPORT,
  },
];

export enum StatusType {
  DRAFT = 'DRAFT',
  IN_REVISION = 'IN_REVISION',
  ON_APPROVAL = 'ON_APPROVAL',
  APPROVED = 'APPROVED',
  SENT = 'SENT',
  CANCELED = 'CANCELED',
  ABANDONED = 'ABANDONED',
  REJECTED = 'REJECTED',
  SUGGESTED = 'SUGGESTED',
}

export enum OrderBlockReason {
  NONE = 'NONE',
  SYNCING_OFFLINE = 'SYNCING_OFFLINE',
  ERROR_SYNCING_OFFLINE = 'ERROR_SYNCING_OFFLINE',
  MASS_ACTION_STATUS_UPDATING = 'MASS_ACTION_STATUS_UPDATING',
}

export enum VirtualOrderStatus {
  MASS_ACTION_STATUS_UPDATING = OrderBlockReason.MASS_ACTION_STATUS_UPDATING,
}

export const orderIntegrationTriggerSelect = [
  {
    label: LangEnum.ORDER_STATUS_ON_APPROVAL,
    value: 'ON_APPROVAL',
  },
  {
    label: LangEnum.ORDER_STATUS_APPROVED,
    value: 'APPROVED',
  },
  {
    label: LangEnum.ORDER_STATUS_SENT,
    value: 'SENT',
  },
];

export enum OrderTransportCondition {
  NONE = 'NONE',
  FREIGHT_TYPE = 'FREIGHT_TYPE',
  FREIGHT_TYPE_AND_SHIPPING_COMPANY = 'FREIGHT_TYPE_AND_SHIPPING_COMPANY',
}

export const orderTransportConditionSelect = [
  {
    label: LangEnum.GLOBAL_NONE,
    value: OrderTransportCondition.NONE,
  },
  {
    label: LangEnum.GLOBAL_FREIGHT_TYPE,
    value: OrderTransportCondition.FREIGHT_TYPE,
  },
  {
    label: LangEnum.ORDER_TRANSPORT_CONDITION_FREIGHT_TYPE_AND_SHIPPING_COMPANY,
    value: OrderTransportCondition.FREIGHT_TYPE_AND_SHIPPING_COMPANY,
  },
];

export enum ReverseOrderSyncIntegrationTypeEnum {
  COMPLETE = 'COMPLETE',
  COMPLETE_WITHOUT_ORDER_INCOME = 'COMPLETE_WITHOUT_ORDER_INCOME',
  ORDER_STATUS = 'ORDER_STATUS',
}

export const reverseOrderSyncIntegrationTypeSelect = [
  {
    label: LangEnum.REVERSE_ORDER_SYNC_INTEGRATION_TYPE_COMPLETE,
    value: ReverseOrderSyncIntegrationTypeEnum.COMPLETE,
  },
  {
    label: LangEnum.REVERSE_ORDER_SYNC_INTEGRATION_TYPE_COMPLETE_WITHOUT_ORDER_INCOME,
    value: ReverseOrderSyncIntegrationTypeEnum.COMPLETE_WITHOUT_ORDER_INCOME,
  },
  {
    label: LangEnum.REVERSE_ORDER_SYNC_INTEGRATION_TYPE_STATUS,
    value: ReverseOrderSyncIntegrationTypeEnum.ORDER_STATUS,
  },
];

export const synchronizedTypesSelect: Array<LangEnumOption> = [
  {
    label: LangEnum.ORDER_SYNC_STATUS_SUCCESS,
    value: 'SUCCESS',
  },
  {
    label: LangEnum.GLOBAL_ERROR,
    value: 'ERROR',
  },
  {
    label: LangEnum.GLOBAL_PROCESSING,
    value: 'PROCESSING',
  },
  {
    label: LangEnum.ORDER_FILTER_NOT_SYNC_LABEL,
    value: 'NOT_SYNCHRONIZED',
  },
  {
    label: LangEnum.ORDER_SYNC_STATUS_SUCCESS_WITH_PAYMENT_ERROR,
    value: 'SUCCESS_WITH_PAYMENT_ERROR',
  },
];

export const Status: { [key: string]: { label: LangEnum; color: string } } = {
  DRAFT: {
    label: LangEnum.ORDER_STATUS_DRAFT,
    color: '#97999b',
  },
  IN_REVISION: {
    label: LangEnum.ORDER_STATUS_IN_REVISION,
    color: '#ff703d',
  },
  SUGGESTED: {
    label: LangEnum.GLOBAL_SUGGESTION,
    color: teceoOrange,
  },
  ON_APPROVAL: {
    label: LangEnum.ORDER_STATUS_ON_APPROVAL,
    color: '#3366ff',
  },
  APPROVED: {
    label: LangEnum.ORDER_STATUS_APPROVED,
    color: '#00daad',
  },
  SENT: {
    label: LangEnum.ORDER_STATUS_SENT,
    color: '#008970',
  },
  CANCELED: {
    label: LangEnum.ORDER_STATUS_CANCELED,
    color: '#242424',
  },
  ABANDONED: {
    label: LangEnum.ORDER_STATUS_ABANDONED,
    color: '#424242',
  },
  REJECTED: {
    label: LangEnum.ORDER_STATUS_REJECTED,
    color: '#242424',
  },
  OFFLINE: {
    label: LangEnum.GLOBAL_OFFLINE,
    color: '#808080',
  },
  ERROR_SYNCING: {
    label: LangEnum.GLOBAL_ERROR,
    color: Theme.palette.error.main,
  },
  SYNCING: {
    label: LangEnum.GLOBAL_SYNCING,
    color: '#FF703D',
  },
  [OrderBlockReason.MASS_ACTION_STATUS_UPDATING]: {
    label: LangEnum.GLOBAL_UPDATING,
    color: '#FC6B4B',
  },
};

export const CustomerStatus: { [key: string]: { label: LangEnum; color: string } } = {
  INACTIVE: {
    label: LangEnum.CUSTOMER_STATUS_INACTIVE,
    color: '#424242',
  },
  PENDING: {
    label: LangEnum.CUSTOMER_STATUS_PENDING,
    color: '#3366ff',
  },
  APPROVED: {
    label: LangEnum.CUSTOMER_STATUS_APPROVED,
    color: '#00daad',
  },
  BLOCKED: {
    label: LangEnum.CUSTOMER_STATUS_BLOCKED,
    color: '#97999b',
  },
  DRAFT: {
    label: LangEnum.CUSTOMER_STATUS_DRAFT,
    color: theme.palette.grey[400],
  },
};

export const synchronizedStatus: { [key: string]: { label: LangEnum; color: string } } = {
  SUCCESS: {
    label: LangEnum.ORDER_SYNC_STATUS_SUCCESS,
    color: '#00daad',
  },
  SUCCESS_RESET: {
    label: LangEnum.ORDER_SYNC_STATUS_SUCCESS_RESET,
    color: '#3366ff',
  },
  ERROR: {
    label: LangEnum.ORDER_SYNC_STATUS_ERROR,
    color: '#f44336',
  },
  NOT_SYNCHRONIZED: {
    label: LangEnum.ORDER_FILTER_NOT_SYNC_LABEL,
    color: '#97999b',
  },
  PROCESSING: {
    label: LangEnum.GLOBAL_INTEGRATION_PROCESSING,
    color: '#97999b',
  },
  PROCESSING_AFTER_RESET: {
    label: LangEnum.GLOBAL_INTEGRATION_PROCESSING,
    color: '#3366ff',
  },
  SUCCESS_WITH_PAYMENT_ERROR: {
    label: LangEnum.ORDER_SYNC_STATUS_SUCCESS_WITH_PAYMENT_ERROR,
    color: teceoWarningMain,
  },
};

export const mobileSynchronizedStatusLabel: { [key: string]: LangEnum } = {
  SUCCESS: LangEnum.GLOBAL_SUCCESS,
  SUCCESS_RESET: LangEnum.GLOBAL_SUCCESS,
  ERROR: LangEnum.GLOBAL_ERROR,
  NOT_SYNCHRONIZED: LangEnum.ORDER_FILTER_NOT_SYNC_LABEL,
  PROCESSING: LangEnum.GLOBAL_PROCESSING,
  PROCESSING_AFTER_RESET: LangEnum.GLOBAL_PROCESSING,
  SUCCESS_WITH_PAYMENT_ERROR: LangEnum.ORDER_SYNC_STATUS_SUCCESS_WITH_PAYMENT_ERROR,
};

export type SplitParams = {
  type: BrandSplitType.CLASSIFICATION_TYPE;
  classificationTypeIds: string[];
};

export type BrandSplitSettings = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  type: BrandSplitType;
  priority: number;
  params: SplitParams;
};

interface IMunicipality {
  id: string;
  name: string;
  code: string;
  uf: string;
}

interface IAddressDTO {
  id: string;
  zipCode: string;
  streetDescription: string;
  streetNumber: string;
  complement?: string | null;
  reference?: string | null;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
  municipality: IMunicipality;
}

interface IAddress {
  address: IAddressDTO;
  id: string;
  principal: boolean;
}

export interface CommonParamsWithOfflineOrder {
  id: string;
  createdAt: Date;
  subTotal: number;
  totalItems: number;
  totalValue: number;
  totalRetail: number;
  totalPieces: number;
  priceTable: PriceTable;
  customer?: Customer;
  seller?: Seller;
  salesRepresentative?: SalesRepresentative;
}

export interface GetOrderGatewayPaymentDTO {
  id: string;
  status: OrderGatewayPaymentStatus;
  paymentUrl: string;
  value: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Order<CanBeFromOffline = false> extends CommonParamsWithOfflineOrder {
  code: string;
  status: CanBeFromOffline extends false ? OrderStatus : OrderStatus | OfflineStatus;
  archived: boolean;
  splitted: boolean;
  brand: GetBrandDTO;
  type: OrderTypeString;
  availability: OrderType[];
  totalDiscountValue: number;
  synchronized: SynchronizedType;
  classification: Classification;
  paymentStatus: OrderPaymentStatus;
  splitSettings: BrandSplitSettings[];
  deliveryWindow?: DeliveryWindow;
  sellerName?: string;
  sellerEmail?: string;
  externalCode?: string;
  addresses?: IAddress[];
  consultant?: Consultant;
  hasBrandIntegrations?: boolean;
  orderGatewayPayments: GetOrderGatewayPaymentDTO[];
  hasInvoices?: boolean;
  signedContracts: SignedContract[];
}

export type OrderWithActions = ListOrdersDTO & { orderActions: OrderAction[] };

export type OrderHistory = {
  id: string;
  newState: OrderStatus | OfflineStatus;
  createdAt: string;
  user?: User;
  reason?: string;
  totalItems?: number;
  totalPieces?: number;
  subTotal?: number;
  orderCode: string;
  parentOrderId: string | null;
  parentOrderCode: string | null;
};

export interface ExportOrderEventCsvParams {
  eventId: string;
  filename: string;
}

export interface GetOrderPaymentHistoryDTO {
  orderGatewayPaymentId: string;
  orderGatewayPaymentHistoryId: string;
  orderGatewayPaymentCreatedAt: string;
  userName?: string;
  userSurname?: string;
  email?: string;
  paymentUrl: string;
  amountToPay: number;
  status: OrderGatewayPaymentStatus;
  errorReason: string | null;
}

export interface ConditionValidationErrors {
  message: string;
  messageCode: LangEnum;
  details?: Omit<ConditionValidationErrors, 'details' | 'extra'>[];
  extra?: {
    [key: string]: string | number | string[];
  };
}

export interface OrderClassification {
  code: string;
  name: string;
}

export interface OrderClassificationType {
  code: string;
  name: string;
  orderClassifications: OrderClassification[];
}

export interface OrderOperationsIntegrationType {
  code: number;
  name: string;
}

export interface OrderBatchItemsType {
  code: string;
  name: string;
}

export interface OrderListType extends Exclude<OrderListSubRows, 'isChild'> {
  baseCode: string;
  isParent: boolean;
  parentSubTotal: number;
  parentTotalValue: number;
  parentTotalItems: number;
  parentTotalDiscount: number;
  parentTotalShippingCost: number;
  parentTotalCharges: number;
  parentTotalPieces: number;
  parentCreatedAt: Date | string;
  parentUpdatedAt: Date | string;
  subRows?: OrderListSubRows[];
}

export interface OrderListSubRows {
  isChild: boolean;
  orderId: string;
  orderCode: string;
  baseCode: string;
  orderExternalCode: string;
  availabilities: AvailabilityEnum[];
  orderType: OrderType;
  sellerId: number;
  sellerName: string;
  sellerEmail: string;
  totalPieces: number;
  orderItemsGrossTotalValue?: number;
  orderItemsTotalDiscountValue?: number;
  orderSubTotal: number;
  orderTotalValue: number;
  orderDiscountValue: number;
  orderTotalCharges?: number;
  orderTotalShippingCost?: number;
  orderCreatedAt: Date | string;
  orderUpdatedAt: Date | string;
  orderIntegratedAt?: Date | string | null;
  orderSplitted: boolean;
  orderSplitSettings: BrandSplitSettings[];
  orderStatus: StatusType | OfflineStatus;
  orderBlockReason?: OrderBlockReason;
  orderVirtualStatus?: VirtualOrderStatus;
  orderArchived: boolean;
  orderPaymentStatus: OrderPaymentStatus;
  orderSynchronized: SynchronizedType;
  orderSalesRepresentativeId: number;
  orderSalesRepresentativeName: string;
  orderLastReason?: string;
  orderCollections?: string[];
  orderCollectionsCodes?: string[];
  deliveryWindow?: DeliveryWindow;
  customerId?: number;
  customerCode?: string;
  customerCommercialName?: string;
  customerCompanyName?: string;
  customerCpfCnpj?: string;
  customerSuframaCode?: string;
  customerIsPj?: boolean;
  customerStoreId?: number;
  customerCity?: string;
  customerState?: string;
  customerStatus?: string;
  brandId: string;
  brandCommercialName: string;
  brandCompanyName: string;
  classification: Classification;
  lastGatewayPaymentStatus: OrderGatewayPaymentStatus | null;
  lastGatewayPaymentUpdatedAt: Date | null;
  requirePaymentGateway?: boolean;
}

export type OrderListWithBaseItem = (OrderListType | OrderListSubRows) & BaseItem;

export type OrderPriceTable = {
  id: string;
  name: string;
  code?: string;
  defaultTable?: boolean;
  priceTableId: string;
  orderId: string;
};

export type OrderItemPriceComposition = OrderItemPriceCompositionItem[];

export type OrderItemPriceCompositionItem = {
  id: string;
  label: string;
  price: number;
};

export type GetOrderViewParams = {
  role?: UserRole;
  canEditOrder?: boolean;
  integrations?: BrandIntegration[];
  orderStatus?: StatusType | OfflineStatus;
  orderIntegrationStatus?: SynchronizedType;
  appEnvironment?: AppEnvironment;
};

export const orderStatusTypeLangEnumMap: Record<StatusType, LangEnum> = {
  [StatusType.APPROVED]: LangEnum.ORDER_STATUS_APPROVED,
  [StatusType.ON_APPROVAL]: LangEnum.ORDER_STATUS_ON_APPROVAL,
  [StatusType.SENT]: LangEnum.ORDER_STATUS_SENT,
  [StatusType.CANCELED]: LangEnum.ORDER_STATUS_CANCELED,
  [StatusType.REJECTED]: LangEnum.ORDER_STATUS_REJECTED,
  [StatusType.DRAFT]: LangEnum.ORDER_STATUS_DRAFT,
  [StatusType.IN_REVISION]: LangEnum.ORDER_STATUS_IN_REVISION,
  [StatusType.SUGGESTED]: LangEnum.ORDER_STATUS_SUGGESTED,
  [StatusType.ABANDONED]: LangEnum.ORDER_STATUS_ABANDONED,
};

export const lostOrderStatuses: StatusType[] = [
  StatusType.CANCELED,
  StatusType.REJECTED,
  StatusType.ABANDONED,
];

export const reverseOrderSyncMappedStatusSelect = [
  StatusType.APPROVED,
  StatusType.CANCELED,
  StatusType.ON_APPROVAL,
  StatusType.REJECTED,
  StatusType.SENT,
];

export enum TotvsOrderStatus {
  IN_PROGRESS = 'InProgress',
  BILLING_RELEASED = 'BillingReleased',
  PARTIALLY_ANSWERED = 'PartiallyAnswered',
  ATTENDED = 'Attended',
  BLOCKED = 'Blocked',
  CANCELED = 'Canceled',
  IN_COMPOSITION = 'InComposition',
  IN_ANALYSIS = 'InAnalysis',
}

export const totvsOrderStatusTypeLangEnumMap: Record<TotvsOrderStatus, LangEnum> = {
  [TotvsOrderStatus.IN_PROGRESS]: LangEnum.GLOBAL_IN_PROGRESS,
  [TotvsOrderStatus.BILLING_RELEASED]: LangEnum.GLOBAL_BILLING_RELEASED,
  [TotvsOrderStatus.PARTIALLY_ANSWERED]: LangEnum.GLOBAL_PARTIALLY_ANSWERED,
  [TotvsOrderStatus.BLOCKED]: LangEnum.GLOBAL_BLOCKED,
  [TotvsOrderStatus.CANCELED]: LangEnum.GLOBAL_CANCELED,
  [TotvsOrderStatus.IN_COMPOSITION]: LangEnum.GLOBAL_IN_COMPOSITION,
  [TotvsOrderStatus.ATTENDED]: LangEnum.GLOBAL_ATTENDED,
  [TotvsOrderStatus.IN_ANALYSIS]: LangEnum.GLOBAL_IN_ANALYSIS,
};
